import React, { useEffect, useReducer } from 'react';
import { AppLayout } from '../layouts/AppLayout';
import { useAuth } from '../contexts/AuthContext';
import IPIWidget from '../components/IPIWidget';
import {
  AlertTriangle, Clock, Flame, TrendingDown, DollarSign,
  Sparkles, Building2, ChevronRight
} from 'lucide-react';

const initialState = { snapshot: null, loading: true, error: null };
function reducer(state, action) {
  switch (action.type) {
    case 'LOADING':  return { ...state, loading: true, error: null };
    case 'SUCCESS':  return { snapshot: action.payload, loading: false, error: null };
    case 'FAILURE':  return { ...state, loading: false, error: action.payload };
    default: return state;
  }
}

/**
 * Visão Executiva — Painel de 30 segundos
 *
 * "Minha gestão está protegida?"
 *
 * Estrutura:
 *   1. Header com IPI gigante
 *   2. 4 KPIs secundários (críticos, vencidos, próximos, reincidências)
 *   3. Resumo IA (Situação Geral / Principais Riscos / Ações Recomendadas)
 *   4. Ranking de Secretarias / Unidades
 */
export default function ExecutiveViewPage() {
  const { api } = useAuth();
  const [state, dispatch] = useReducer(reducer, initialState);
  const { snapshot, loading, error } = state;

  useEffect(() => {
    let cancelled = false;
    dispatch({ type: 'LOADING' });
    api.get('/executive/snapshot')
      .then((res) => { if (!cancelled) dispatch({ type: 'SUCCESS', payload: res.data }); })
      .catch((e) => { if (!cancelled) dispatch({ type: 'FAILURE', payload: e?.response?.data?.detail || 'Erro ao carregar snapshot' }); });
    return () => { cancelled = true; };
  }, [api]);

  const fmtBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v || 0);

  return (
    <AppLayout>
      <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50" data-testid="executive-view-page">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12 space-y-8">
          {/* Header */}
          <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 mb-3">
                <Sparkles className="w-3.5 h-3.5 text-blue-600" />
                <span className="text-xs font-bold uppercase tracking-wider text-blue-700">Visão Executiva — 30 segundos</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight" data-testid="executive-title">
                Sua gestão está protegida?
              </h1>
              <p className="text-slate-600 mt-2 text-sm md:text-base">
                Painel institucional consolidado com base nos dados mais recentes do seu órgão.
              </p>
            </div>
            <div className="text-xs text-slate-500 text-right">
              {snapshot?.generated_at && (
                <>
                  Atualizado em<br />
                  <strong className="text-slate-700">{new Date(snapshot.generated_at).toLocaleString('pt-BR')}</strong>
                </>
              )}
            </div>
          </header>

          {/* IPI principal */}
          <IPIWidget />

          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="executive-error">
              {error}
            </div>
          )}

          {loading && !snapshot && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[1,2,3,4].map((i) => (
                <div key={i} className="h-32 rounded-xl bg-white border border-slate-200 animate-pulse" />
              ))}
            </div>
          )}

          {snapshot && (
            <>
              {/* 4 KPIs secundários */}
              <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KpiCard
                  icon={Flame}
                  label="Atos Críticos"
                  value={snapshot.kpis.critical_open}
                  hint="Severidade CRITICAL em aberto"
                  tone={snapshot.kpis.critical_open > 0 ? 'red' : 'green'}
                  testid="kpi-critical"
                />
                <KpiCard
                  icon={AlertTriangle}
                  label="Atos Vencidos"
                  value={snapshot.kpis.delayed_open}
                  hint="Prazo legal expirado"
                  tone={snapshot.kpis.delayed_open > 0 ? 'red' : 'green'}
                  testid="kpi-delayed"
                />
                <KpiCard
                  icon={Clock}
                  label="Próximos do Vencimento"
                  value={snapshot.kpis.approaching_deadline_10d}
                  hint="Vencem em 10 dias"
                  tone={snapshot.kpis.approaching_deadline_10d > 0 ? 'orange' : 'green'}
                  testid="kpi-approaching"
                />
                <KpiCard
                  icon={DollarSign}
                  label="Risco Financeiro"
                  value={fmtBRL(snapshot.kpis.financial_exposure_brl)}
                  hint="Exposição potencial"
                  tone={snapshot.kpis.financial_exposure_brl > 0 ? 'red' : 'green'}
                  big
                  testid="kpi-financial"
                />
              </section>

              {/* AI Narrative */}
              <section className="rounded-2xl border-2 border-blue-100 bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-6 md:p-8 shadow-sm" data-testid="ai-narrative">
                <div className="flex items-start gap-4 mb-5">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center shadow-md shrink-0">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl md:text-2xl font-extrabold text-slate-900">{snapshot.ai_narrative.title}</h2>
                    <p className="text-xs text-blue-700/80 font-semibold uppercase tracking-wider mt-1">Resumo Executivo · IA Institucional</p>
                  </div>
                </div>
                <div className="space-y-3 text-slate-700 text-[15px] leading-relaxed pl-0 md:pl-16">
                  {snapshot.ai_narrative.paragraphs.map((p, i) => (
                    <p key={i} data-testid={`ai-narrative-paragraph-${i}`}>{p}</p>
                  ))}
                </div>
                {snapshot.ai_narrative.actions?.length > 0 && (
                  <div className="mt-6 md:ml-16">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Ações Recomendadas</p>
                    <ul className="space-y-2">
                      {snapshot.ai_narrative.actions.map((a, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-800" data-testid={`ai-action-${i}`}>
                          <div className="w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">{i+1}</div>
                          <span>{a}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </section>

              {/* Ranking de Secretarias / Unidades */}
              <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm" data-testid="units-ranking">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h2 className="text-xl md:text-2xl font-extrabold text-slate-900 flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-slate-700" />
                      Ranking de Unidades
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">Score de proteção institucional por unidade (0-100).</p>
                  </div>
                </div>

                {snapshot.units_ranking?.length === 0 ? (
                  <p className="text-sm text-slate-500 italic">Sem unidades responsáveis registradas.</p>
                ) : (
                  <div className="space-y-3">
                    {snapshot.units_ranking.map((u, idx) => (
                      <UnitRow key={u.unit + idx} rank={idx + 1} unit={u} testid={`unit-row-${idx}`} />
                    ))}
                  </div>
                )}
              </section>

              {/* Reincidências */}
              {snapshot.kpis.reincidence_90d > 0 && (
                <section className="rounded-2xl border-2 border-amber-200 bg-amber-50 p-6 flex items-start gap-4" data-testid="reincidence-alert">
                  <TrendingDown className="w-7 h-7 text-amber-600 shrink-0 mt-1" />
                  <div>
                    <h3 className="font-extrabold text-amber-900 text-lg">{snapshot.kpis.reincidence_90d} reincidência(s) identificada(s) nos últimos 90 dias</h3>
                    <p className="text-sm text-amber-800 mt-1">
                      Atos do mesmo tipo se repetindo na mesma unidade. Recomendamos investigação de causa-raiz para evitar sanção majorada.
                    </p>
                  </div>
                </section>
              )}

              {/* Footer institucional */}
              <footer className="text-center text-xs text-slate-400 pt-4 pb-2">
                BlueGov — Plataforma de Conformidade e Proteção Institucional
              </footer>
            </>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

function KpiCard({ icon: Icon, label, value, hint, tone = 'slate', big = false, testid }) {
  const toneMap = {
    red:    { ring: 'ring-red-200',     bg: 'bg-red-50',     text: 'text-red-700',     icon: 'text-red-600',     accent: 'bg-red-600' },
    orange: { ring: 'ring-orange-200',  bg: 'bg-orange-50',  text: 'text-orange-700',  icon: 'text-orange-600',  accent: 'bg-orange-600' },
    green:  { ring: 'ring-emerald-200', bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'text-emerald-600', accent: 'bg-emerald-600' },
    slate:  { ring: 'ring-slate-200',   bg: 'bg-slate-50',   text: 'text-slate-700',   icon: 'text-slate-600',   accent: 'bg-slate-700' },
  };
  const t = toneMap[tone] || toneMap.slate;
  return (
    <div className={`rounded-xl border border-slate-200 bg-white p-5 ring-1 ${t.ring} hover:shadow-md transition-all`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-9 h-9 rounded-lg ${t.bg} flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${t.icon}`} />
        </div>
        <p className="text-xs uppercase tracking-wider font-bold text-slate-500">{label}</p>
      </div>
      <p className={`${big ? 'text-2xl md:text-3xl' : 'text-3xl md:text-4xl'} font-extrabold ${t.text} leading-none`}>{value}</p>
      <p className="text-xs text-slate-500 mt-2">{hint}</p>
    </div>
  );
}

function UnitRow({ rank, unit, testid }) {
  const score = unit.score || 0;
  const color = score >= 85 ? 'emerald' : score >= 60 ? 'amber' : 'red';
  const colorMap = {
    emerald: { bar: 'bg-emerald-500', text: 'text-emerald-700' },
    amber:   { bar: 'bg-amber-500',   text: 'text-amber-700' },
    red:     { bar: 'bg-red-500',     text: 'text-red-700' },
  };
  const c = colorMap[color];
  return (
    <div className="flex items-center gap-4 p-3 rounded-lg hover:bg-slate-50 transition-colors" data-testid={testid}>
      <div className="w-7 h-7 rounded-md bg-slate-100 text-slate-700 font-bold text-xs flex items-center justify-center">{rank}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900 truncate">{unit.unit}</p>
        <p className="text-xs text-slate-500">
          {unit.total} atos · {unit.open_late} vencido(s) · {unit.critical_open} crítico(s)
        </p>
      </div>
      <div className="w-32 hidden md:block">
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
          <div className={`h-full ${c.bar} rounded-full transition-all duration-700`} style={{ width: `${score}%` }} />
        </div>
      </div>
      <div className={`text-lg font-extrabold ${c.text} w-12 text-right`}>{score.toFixed(0)}</div>
      <ChevronRight className="w-4 h-4 text-slate-300 hidden md:block" />
    </div>
  );
}
